import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { EmbeddedPageSkeleton } from "@/components/skeleton-page/EmbeddedPageSkeleton";
import { useIsInsidePage } from "@/components/shared/Page";
import { Skeleton } from "@/components/ui/skeleton";
import { pageInnerStackClassName, pageStackClassName } from "@/components/layout/pageLayout";
import { cn } from "@/lib/utils";

function useSkeletonPageStackClassName(className?: string): string {
  const isInsidePage = useIsInsidePage();
  return cn(isInsidePage ? pageInnerStackClassName : pageStackClassName, className);
}

type SkeletonPageListProps = {
  variant: "list";
  showPrimaryAction?: boolean;
  showSearch?: boolean;
  showHeader?: boolean;
  grid?: ReactNode;
  className?: string;
};

type SkeletonPageFormProps = {
  variant: "form";
  showBackLink?: boolean;
  children?: ReactNode;
  className?: string;
};

type SkeletonPageShellProps = {
  variant: "shell";
  className?: string;
};

type SkeletonPageEmbeddedProps = {
  variant: "embedded";
  tallSummary?: boolean;
  className?: string;
};

export type SkeletonPageProps =
  | SkeletonPageListProps
  | SkeletonPageFormProps
  | SkeletonPageShellProps
  | SkeletonPageEmbeddedProps;

function SkeletonPageDefaultFormBody(): ReactElement {
  return (
    <>
      <div className="space-y-2">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="min-h-24 w-full rounded-md" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <div className="flex gap-3">
          <Skeleton className="h-8 w-14 shrink-0 rounded-lg" />
          <Skeleton className="h-8 flex-1" />
        </div>
      </div>
      <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
        <Skeleton className="h-10 w-full sm:w-24" />
        <Skeleton className="h-10 w-full sm:w-28" />
      </div>
    </>
  );
}

/** Form field blocks only — for {@link FormFlowSkeleton} (footer actions live in the flow chrome). */
export function SkeletonFormFieldBlocks(): ReactElement {
  return (
    <>
      <div className="space-y-2">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="min-h-24 w-full rounded-md" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <div className="flex gap-3">
          <Skeleton className="h-8 w-14 shrink-0 rounded-lg" />
          <Skeleton className="h-8 flex-1" />
        </div>
      </div>
    </>
  );
}

export function SkeletonPage(props: SkeletonPageProps): ReactElement {
  const { t } = useTranslation();
  const stackClassName = useSkeletonPageStackClassName(props.className);

  if (props.variant === "shell") {
    return (
      <div className={stackClassName} aria-busy="true">
        <span className="sr-only">{t("common.loading")}</span>
        <Skeleton className="h-8 w-52 max-w-full rounded-md" />
        <div className="flex flex-col gap-4">
          <Skeleton className="h-4 w-full max-w-2xl rounded-md" />
          <Skeleton className="h-4 w-full max-w-xl rounded-md" />
          <Skeleton className="min-h-48 w-full max-w-4xl rounded-lg" />
        </div>
      </div>
    );
  }

  if (props.variant === "embedded") {
    return <EmbeddedPageSkeleton tallSummary={props.tallSummary} className={props.className} />;
  }

  if (props.variant === "list") {
    const { showPrimaryAction = false, showSearch = false, showHeader = true, grid } = props;

    return (
      <div className={stackClassName} aria-busy="true">
        <span className="sr-only">{t("common.loading")}</span>
        {showHeader ? (
          <header className="flex flex-wrap items-center justify-between gap-4">
            <Skeleton className="h-8 w-48 max-w-[min(100%,12rem)] shrink-0 rounded-md" />
            {showPrimaryAction ? (
              <div className="flex w-full shrink-0 justify-end sm:w-auto">
                <Skeleton className="h-10 w-full shrink-0 rounded-md sm:w-52" />
              </div>
            ) : null}
          </header>
        ) : null}

        <section className="flex flex-col gap-4">
          {showSearch ? (
            <Skeleton className="h-10 w-full rounded-md sm:max-w-xs sm:shrink-0" />
          ) : null}
          {grid}
        </section>
      </div>
    );
  }

  const { showBackLink = true, children } = props;

  return (
    <div className={stackClassName} aria-busy="true">
      <span className="sr-only">{t("common.loading")}</span>
      <div className="flex flex-col gap-4">
        {showBackLink ? <Skeleton className="h-9 w-44 max-w-full rounded-md" /> : null}
        <header className="flex flex-col gap-1">
          <Skeleton className="h-8 w-72 max-w-full rounded-md" />
        </header>
      </div>

      <div className="flex flex-col gap-4">{children ?? <SkeletonPageDefaultFormBody />}</div>
    </div>
  );
}
